import Phaser from "phaser";
import { audioManager } from "../systems/AudioManager";
import { bodyStyle } from "./theme";

export type MenuButtonVariant = "primary" | "secondary";

export class MenuButton {
  readonly container: Phaser.GameObjects.Container;
  private image: Phaser.GameObjects.Image;
  private label: Phaser.GameObjects.Text;
  private accent: Phaser.GameObjects.Sprite | null = null;
  private selected = false;
  private enabled = true;
  private readonly variant: MenuButtonVariant;
  private readonly scene: Phaser.Scene;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    private readonly onClick: () => void,
    width = 236,
    variant: MenuButtonVariant = "primary",
  ) {
    this.scene = scene;
    this.variant = variant;
    this.image = scene.add.image(0, 0, this.idleTexture());
    const height = Math.round(width * 0.32);
    this.image.setDisplaySize(width, height);
    this.label = scene.add.text(0, -1, text, bodyStyle(text.length > 14 ? 18 : 20));
    this.label.setOrigin(0.5);
    const children: Phaser.GameObjects.GameObject[] = [this.image, this.label];
    if (scene.textures.exists("menu-selection-accent")) {
      this.accent = scene.add.sprite(0, 0, "menu-selection-accent", 0);
      this.accent.setDisplaySize(width + 10, height + 10).setVisible(false).setAlpha(0.7);
      children.unshift(this.accent);
    }
    this.container = scene.add.container(x, y, children);
    this.container.setSize(width, height);
    this.container.setInteractive({ useHandCursor: true });
    this.container.on("pointerover", () => this.setSelected(true));
    this.container.on("pointerout", () => this.setSelected(false));
    this.container.on("pointerdown", () => {
      if (!this.enabled) {
        return;
      }
      this.image.setTexture("button-pressed");
    });
    this.container.on("pointerup", () => {
      if (!this.enabled) {
        return;
      }
      this.activate();
    });
  }

  private idleTexture(): string {
    return this.variant === "secondary" ? "button-secondary" : "button-primary";
  }

  private focusedTexture(): string {
    if (this.variant === "secondary") {
      return this.scene.textures.exists("button-secondary-focused")
        ? "button-secondary-focused"
        : "button-focused";
    }
    return "button-focused";
  }

  setSelected(selected: boolean, playSound = true): void {
    if (this.selected === selected) {
      return;
    }
    this.selected = selected;
    if (!this.enabled) {
      this.image.setTexture("button-disabled");
      this.accent?.setVisible(false);
      return;
    }
    this.image.setTexture(selected ? this.focusedTexture() : this.idleTexture());
    if (this.accent) {
      this.accent.setVisible(selected);
      if (selected && this.scene.anims.exists("menu-selection-accent")) {
        this.accent.play("menu-selection-accent", true);
      }
    }
    if (selected && playSound) {
      audioManager.playSfx("ui-move");
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.image.setTexture(enabled ? (this.selected ? this.focusedTexture() : this.idleTexture()) : "button-disabled");
    this.label.setAlpha(enabled ? 1 : 0.55);
    this.accent?.setVisible(enabled && this.selected);
    if (enabled) {
      this.container.setInteractive({ useHandCursor: true });
    } else {
      this.container.disableInteractive();
    }
  }

  activate(): void {
    if (!this.enabled) {
      return;
    }
    audioManager.playSfx("ui-confirm");
    this.onClick();
  }

  isSelected(): boolean {
    return this.selected;
  }
}

export function bindMenuKeys(
  scene: Phaser.Scene,
  buttons: MenuButton[],
  initialIndex = 0,
  confirm = true,
): () => void {
  if (buttons.length === 0) {
    return () => undefined;
  }
  let index = Math.min(initialIndex, buttons.length - 1);
  buttons[index]?.setSelected(true, false);

  const keyboard = scene.input.keyboard;
  if (!keyboard) {
    return () => undefined;
  }

  const move = (delta: number): void => {
    buttons[index]?.setSelected(false, false);
    index = (index + buttons.length + delta) % buttons.length;
    buttons[index]?.setSelected(true);
  };

  const onUp = (): void => move(-1);
  const onDown = (): void => move(1);
  const onLeft = (): void => move(-1);
  const onRight = (): void => move(1);
  const onEnter = (): void => buttons[index]?.activate();
  const onSpace = (): void => buttons[index]?.activate();

  keyboard.on("keydown-UP", onUp);
  keyboard.on("keydown-DOWN", onDown);
  keyboard.on("keydown-LEFT", onLeft);
  keyboard.on("keydown-RIGHT", onRight);
  if (confirm) {
    keyboard.on("keydown-ENTER", onEnter);
    keyboard.on("keydown-SPACE", onSpace);
  }

  return () => {
    keyboard.off("keydown-UP", onUp);
    keyboard.off("keydown-DOWN", onDown);
    keyboard.off("keydown-LEFT", onLeft);
    keyboard.off("keydown-RIGHT", onRight);
    if (confirm) {
      keyboard.off("keydown-ENTER", onEnter);
      keyboard.off("keydown-SPACE", onSpace);
    }
  };
}
